import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/lib/auth';
import { useParams } from 'react-router-dom';
import { Loader2, TrendingUp } from 'lucide-react';
import {
  ResponsiveContainer,
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
} from 'recharts';
import { api } from '@/lib/api';
import { Section } from '@/components/Section';
import { Empty } from '@/components/Empty';
import { KpiCard } from '@/components/KpiCard';

export function FinancePage() {
  const { slug } = useParams<{ slug: string }>();
  const { getToken } = useAuth();

  const financeQuery = useQuery({
    queryKey: ['finance', slug],
    queryFn: () => api.getFinance(slug!, () => getToken()),
  });

  if (financeQuery.isPending) {
    return (
      <div className="flex items-center gap-2 text-ducens-muted">
        <Loader2 className="w-4 h-4 animate-spin" /> Chargement...
      </div>
    );
  }

  if (!financeQuery.data) {
    return <Empty Icon={TrendingUp} title="Pas de données financières" />;
  }

  const data = financeQuery.data;
  const ca12m = data.monthly.reduce((s, m) => s + m.ca_eur, 0);
  const profit12m = data.monthly.reduce((s, m) => s + m.profit_eur, 0);
  const lastMonth = data.monthly[data.monthly.length - 1]!;

  return (
    <>
      <div className="mb-6">
        <h1 className="text-2xl font-semibold leading-tight">Finance</h1>
        <p className="text-sm text-ducens-muted mt-1">
          Revenus, marges, projections sur 12 mois.
        </p>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <KpiCard label="CA 12 mois" value={ca12m.toLocaleString('fr-FR')} unit="€" />
        <KpiCard label="Profit 12 mois" value={profit12m.toLocaleString('fr-FR')} unit="€" />
        <KpiCard label="MRR mois en cours" value={lastMonth.ca_eur.toLocaleString('fr-FR')} unit="€" />
        <KpiCard label="Marge brute" value="68" unit="%" helper="moyenne pondérée" />
      </div>

      <Section title="Évolution mensuelle CA + coûts + profit" description="12 mois roulants">
        <div className="bg-white border border-ducens-border rounded-lg p-4">
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={data.monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0d8" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} />
              <YAxis tick={{ fontSize: 11 }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="ca_eur" name="CA (€)" stroke="#1a1a1a" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="costs_eur" name="Coûts (€)" stroke="#DC2626" strokeWidth={2} dot={false} />
              <Line type="monotone" dataKey="profit_eur" name="Profit (€)" stroke="#16A34A" strokeWidth={2} dot={false} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Section>

      <Section title="CA par canal" description="Répartition mois en cours (€)">
        <div className="bg-white border border-ducens-border rounded-lg p-4">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={data.channels} layout="vertical" margin={{ left: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e0e0d8" horizontal={false} />
              <XAxis type="number" tick={{ fontSize: 11 }} />
              <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={130} />
              <Tooltip />
              <Bar dataKey="value" fill="#F7D87C" radius={[0, 4, 4, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Section>
    </>
  );
}
